import styled from '@emotion/styled';
import React from 'react';

import {mixinFocus} from '@ELib/styles/mixins';
import {classy} from '@ELib/utilities/HelperFunctions';

import {EIconButton, EIconButtonProps} from './EIconButtons';

export const EHeaderButton = ({
  contentFlag,
  attentionFlag,
  warningFlag,
  children,
  isActive,
  ...props
}: {
  contentFlag?: {content: string | number; mode: 'informative' | 'warning'};
  warningFlag?: boolean;
  attentionFlag?: boolean;
  children: React.ReactElement | React.ReactElement[];
  isActive?: boolean;
} & Omit<
  EIconButtonProps,
  'color' | 'disableMenuPortal'
>): React.ReactElement => (
  <DownStateWrapper>
    <EHeaderButtonWrapper
      className={`EHeaderButton ${classy({
        active: Boolean(isActive),
        disabled: Boolean(props.disabled),
      })}`}
    >
      <EIconButton {...props} disableMenuPortal>
        {children}
        {attentionFlag && (
          <AttentionFlag
            className={classy({reposition: Boolean(warningFlag)})}
          />
        )}
        {contentFlag && (
          <ContentAttentionFlag
            className={classy({
              informative: contentFlag.mode === 'informative',
              warning: contentFlag.mode === 'warning',
            })}
          >
            {contentFlag.content}
          </ContentAttentionFlag>
        )}
        {warningFlag && <WarningFlag />}
      </EIconButton>
    </EHeaderButtonWrapper>
    <DownState downStateActive={isActive} />
  </DownStateWrapper>
);

const DownStateWrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  gap: var(--gutter-little);
`;

const DownState = styled.div<{downStateActive?: boolean}>`
  border-left: var(--gutter-small) solid transparent;
  border-right: var(--gutter-small) solid transparent;

  border-bottom: var(--gutter-small) solid
    ${({downStateActive}) =>
      downStateActive ? 'var(--color-core-white)' : 'transparent'};
`;

const EHeaderButtonWrapper = styled.span`
  background-color: var(--button-small-bg-color);
  border-radius: 50%;

  && svg {
    fill: var(--button-small-content-color);
    color: var(--button-small-content-color);
  }

  &.active,
  :hover {
    //override the default button hover for svgs here
    && svg {
      fill: var(--button-small-bg-color);
      //work around as shelf icon is not as the other icons
      color: var(--button-small-bg-color);
    }
  }

  &.active {
    background-color: var(--color-state-active);
  }

  :hover {
    background-color: var(--color-state-hover);
  }

  &.disabled {
    pointer-events: none;
    && svg {
      color: var(--color-buttons-primary-inactive);
    }
  }

  .MuiMenu-list {
    background-color: var(--color-buttons-primary-active);
    font-weight: var(--font-weight-paragraph-title);
  }

  .Mui-focusVisible {
    ${mixinFocus};
    border-radius: 50%;
  }

  .EButton-Menu-Item {
    color: var(--color-core-white);
    text-transform: uppercase;

    :hover {
      color: var(--color-core-white);
      background-color: var(--color-buttons-primary-hover);
    }
  }

  .EMenu-Section-Split {
    background-color: var(--color-core-white);
    margin: var(--gutter-little) 0;
  }
`;

const ContentAttentionFlag = styled.div`
  min-width: 16px;
  height: 16px;
  font-size: var(--font-size-tiny);
  padding: var(--gutter-very-tiny) var(--gutter-tiny);
  display: flex;
  justify-content: center;
  align-items: center;
  color: var(--color-core-white);
  border-radius: 8px;
  position: absolute;
  left: 24px;
  top: 0;

  &.informative {
    background-color: var(--color-notification-resolvable);
  }

  &.warning {
    background-color: var(--color-notification-important);
  }
`;

const AttentionFlag = styled.div`
  width: 12px;
  height: 12px;
  background-color: var(--color-notification-informative);
  border-radius: 50%;
  position: absolute;
  left: 18px;
  bottom: 24px;

  .reposition {
    left: 24px;
    bottom: 18px;
  }
`;

const WarningFlag = styled.div`
  width: 14px;
  height: 14px;
  background-color: var(--color-notification-important);
  border-radius: 50%;
  position: absolute;
  left: 18px;
  bottom: 22px;
  display: flex;
  justify-content: center;
  align-items: center;

  :after {
    color: var(--color-core-white);
    font-size: var(--font-size-tiny);
    content: '!';
  }
`;
